import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { TranscriptionSegment } from './useTranscription';

/**
 * 轉錄記錄（資料庫模型）
 */
export interface Transcription {
  id: string;
  user_id: string;
  recording_id: string;
  script_id: string | null;
  segments: TranscriptionSegment[];
  full_text: string | null;
  language: 'zh' | 'en' | 'ja' | 'ko' | 'yue';
  duration: number | null;
  created_at: string;
  updated_at: string;
}

const VALID_LANGUAGES = ['zh', 'en', 'ja', 'ko', 'yue'] as const;
type ValidLanguage = typeof VALID_LANGUAGES[number];

/**
 * 轉錄結果儲存 Hook
 * 處理轉錄結果的 CRUD 操作
 */
export function useTranscriptionStorage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 儲存/更新轉錄（UPSERT by recording_id）
   */
  const saveTranscription = useCallback(async (
    recordingId: string,
    segments: TranscriptionSegment[],
    options?: {
      scriptId?: string;
      language?: string;
      duration?: number;
      fullText?: string;
    }
  ): Promise<Transcription | null> => {
    if (!user) {
      toast({
        title: '請先登入',
        variant: 'destructive',
      });
      return null;
    }

    if (!recordingId) {
      toast({
        title: '儲存失敗',
        description: '缺少錄音 ID，請稍後再試',
        variant: 'destructive',
      });
      return null;
    }

    // 語言驗證
    const lang: ValidLanguage = VALID_LANGUAGES.includes(options?.language as ValidLanguage)
      ? (options!.language as ValidLanguage)
      : 'zh';

    // full_text：中日韓粵無空格，英文有空格
    const separator = ['zh', 'ja', 'ko', 'yue'].includes(lang) ? '' : ' ';
    const fullText = options?.fullText || segments.map(s => s.text).join(separator);

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('transcriptions' as any)
        .upsert({
          user_id: user.id,
          recording_id: recordingId,
          script_id: options?.scriptId || null,
          segments,  // JSONB 直接存陣列
          full_text: fullText,
          language: lang,
          duration: options?.duration || null,
        }, { onConflict: 'recording_id' })
        .select()
        .single();

      if (dbError) throw dbError;

      toast({ title: '字幕已儲存' });
      return data as Transcription;
    } catch (err) {
      const message = err instanceof Error ? err.message : '儲存失敗';
      setError(message);
      toast({
        title: '儲存失敗',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * 載入轉錄（by recording_id）
   */
  const fetchTranscription = useCallback(async (
    recordingId: string
  ): Promise<Transcription | null> => {
    if (!user) return null;

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('transcriptions' as any)
        .select('*')
        .eq('recording_id', recordingId)
        .maybeSingle();

      if (dbError) throw dbError;

      // JSONB 自動解析為陣列
      return data as Transcription;
    } catch (err) {
      const message = err instanceof Error ? err.message : '載入失敗';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * 更新單一段落
   */
  const updateSegment = useCallback(async (
    transcriptionId: string,
    segmentId: number,
    updates: Partial<TranscriptionSegment>
  ): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      // 載入當前資料
      const { data: current, error: fetchError } = await supabase
        .from('transcriptions' as any)
        .select('segments, language')
        .eq('id', transcriptionId)
        .single();

      if (fetchError || !current) {
        throw new Error('找不到轉錄記錄');
      }

      // 更新指定段落
      const segments = (current.segments as TranscriptionSegment[]).map(s =>
        s.id === segmentId ? { ...s, ...updates } : s
      );

      // 重新生成 full_text
      const separator = ['zh', 'ja', 'ko', 'yue'].includes(current.language) ? '' : ' ';
      const fullText = segments.map(s => s.text).join(separator);

      // 存回
      const { error: updateError } = await supabase
        .from('transcriptions' as any)
        .update({ segments, full_text: fullText })
        .eq('id', transcriptionId);

      if (updateError) throw updateError;

      toast({ title: '段落已更新' });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新失敗';
      setError(message);
      toast({
        title: '更新失敗',
        description: message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * 刪除轉錄
   */
  const deleteTranscription = useCallback(async (
    transcriptionId: string
  ): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      const { error: dbError } = await supabase
        .from('transcriptions' as any)
        .delete()
        .eq('id', transcriptionId);

      if (dbError) throw dbError;

      toast({ title: '字幕已刪除' });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '刪除失敗';
      setError(message);
      toast({
        title: '刪除失敗',
        description: message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  return {
    saveTranscription,
    fetchTranscription,
    updateSegment,
    deleteTranscription,
    loading,
    error,
  };
}
